import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Loader2, KeyRound } from 'lucide-react';
import api from '@/lib/api';

function formatError(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(' ');
  return 'Something went wrong. Please try again.';
}

export default function StaffRegister() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!code.trim()) { setError('Please enter your staff code'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register-staff', {
        code: code.trim(), email, password, first_name: firstName, last_name: lastName, phone: phone || null
      });
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      window.location.href = '/';
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Registration failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="staff-register-page">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold font-heading text-slate-900">RentalHub</span>
        </div>

        <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <KeyRound className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">Enter the staff code provided by your admin to create your account. Your role will be assigned automatically.</p>
        </div>

        <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-1">Staff Registration</h3>
        <p className="text-sm text-slate-500 mb-6">Use your invite code to join the team</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="staff-register-error">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">Staff Code</Label>
            <Input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. AB12CD34"
              className="mt-1.5 text-center text-lg font-mono tracking-widest"
              maxLength={8}
              required
              data-testid="staff-code-input"
            />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-slate-700">First Name</Label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First" className="mt-1.5" required data-testid="staff-firstname-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Last Name</Label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last" className="mt-1.5" required data-testid="staff-lastname-input" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" required data-testid="staff-email-input" />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Phone (optional)</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+27 ..." className="mt-1.5" data-testid="staff-phone-input" />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" className="mt-1.5" required data-testid="staff-password-input" />
          </div>

          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid="staff-register-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Staff Account
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="back-to-login-link">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
